/* ══ PERMISSION SYSTEM ═════════════════════════════════════ */

import { TASK_TAXONOMY_SEED } from "../state/seed";

/* ── Task visibility helpers ── */
/* Task visibility helper — determines if a user can see/edit a task based on role rules */
function _locCheck(tk,user,locRule){
  if(!locRule||locRule==="all")return true;
  if(locRule==="none")return false;
  /* own_location — task has no location OR matches user's assigned/allowed locations */
  if(!tk.locId)return true;
  if(tk.locId===(user.assignedLoc||""))return true;
  if((user.allowedLocs||[]).length>0&&(user.allowedLocs||[]).includes(tk.locId))return true;
  return false;
}
function canViewTask(tk,user,visRules,users,projects){
  if(!user)return true;
  const role=user.role||"owner";
  if(role==="owner")return true;
  /* Project membership gate: project tasks are visible only to project
     members (when the project has explicit user members). Mirrors the
     rule enforced by projectCan() so project tasks don't leak through
     Execution's role-based visibility. */
  if(tk.sourceModule==="Projects"){
    const projectId=(tk.contextPayload||{}).projectId;
    if(projectId&&Array.isArray(projects)){
      const proj=projects.find(p=>p.id===projectId);
      if(proj){
        const userMembers=(proj.people||[]).filter(pp=>pp.type==="user");
        if(userMembers.length>0&&!userMembers.some(pp=>pp.userId===user.id))return false;
      }
    }
  }
  const rule=visRules[role]||{view:"assigned_only"};
  const v=rule.view||"assigned_only";
  let pass=true;
  if(v==="all")pass=true;
  else if(v==="none")return false;
  else if(v==="assigned_only")pass=tk.assignedTo===user.id;
  else if(v==="assigned_and_created")pass=tk.assignedTo===user.id||tk.createdBy===user.id;
  else if(v==="all_except_owner_private"){
    const owner=users.find(u=>u.role==="owner");
    if(owner&&tk.createdBy===owner.id&&tk.assignedTo===owner.id)pass=false;
  }
  if(!pass)return false;
  return _locCheck(tk,user,rule.locationView||"all");
}
function canEditTask(tk,user,visRules){
  if(!user)return true;
  const role=user.role||"owner";
  if(role==="owner")return true;
  const rule=visRules[role]||{edit:"assigned_only"};
  const e=rule.edit||"assigned_only";
  let pass=true;
  if(e==="all")pass=true;
  else if(e==="none")return false;
  else if(e==="assigned_only")pass=tk.assignedTo===user.id;
  else if(e==="assigned_and_created")pass=tk.assignedTo===user.id||tk.createdBy===user.id;
  else if(e==="own_only")pass=tk.createdBy===user.id;
  if(!pass)return false;
  return _locCheck(tk,user,rule.locationEdit||"all");
}

/* ── Module permission defaults & checks ── */
const STAFF_PERM_ACTIONS=["viewStaff","addStaff","editStaff","managePayroll","approveLeave","approveLoans","manageDocs"];
const STAFF_DEFAULT_PERMS={viewStaff:["owner"],addStaff:["owner"],editStaff:["owner"],managePayroll:["owner"],approveLeave:["owner"],approveLoans:["owner"],manageDocs:["owner"]};

/* ── Staff workspace view capabilities ─────────────────────
   These gate what a staff user sees in the unified StaffView.
   Each capability corresponds to one tab / panel. Workspace owners
   can override per-role via state.staffViewPermissions (same three-layer
   default → platform → workspace pattern as staffCan()). */
const STAFF_VIEW_ACTIONS=[
  "viewOwnTasks",
  "viewOwnRides","fulfillGroceryRuns",
  "requestGroceries","viewOwnGroceryHistory",
  "logOwnExpenses","viewStaffSuppliers",
  "requestOwnLeave","viewOwnProfile",
];
/* Defaults use the "*staff" wildcard meaning "any Staff-group user" —
   platform-defined or workspace-defined roles alike. This is the whole
   point of the unified staff workspace: capabilities are permission-
   based, not role-based. An owner who wants to restrict a capability
   flips specific roles off via the Staff Workspace Access matrix,
   which writes an explicit role list that overrides this wildcard.

   "viewOwnTasks", "requestOwnLeave" and "viewOwnProfile" are also
   universal (see UNIVERSAL_STAFF_VIEW_CAPABILITIES below). */
const STAFF_VIEW_DEFAULT_PERMS={
  viewOwnRides:          ["*staff"],
  fulfillGroceryRuns:    ["*staff"],
  requestGroceries:      ["*staff"],
  viewOwnGroceryHistory: ["*staff"],
  logOwnExpenses:        ["*staff"],
  viewStaffSuppliers:    ["*staff"],
};

/* Capabilities every Staff-group user gets by default — regardless of role id.
   These are the baseline rights that make a fresh custom role (e.g. "gardener"
   added by a workspace owner) immediately usable without any permission config.
   Richer capabilities still go through the normal allow-list check.

   viewOwnTasks is universal because the Task Engine already filters by
   assignedTo — a staff user with no tasks assigned simply sees an empty
   list, so there's no safety reason to gate the tab itself. */
const UNIVERSAL_STAFF_VIEW_CAPABILITIES=new Set(["viewOwnTasks","requestOwnLeave","viewOwnProfile"]);

/* Humanize a role id into a display label (e.g. "staff_driver" → "Staff Driver") */
function _humanizeRoleId(id){
  if(!id)return "";
  return id.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase());
}

/* Merged list of loginable user profiles available in this workspace.
   Platform-defined profiles come first (canonical), then any workspace-defined
   staff roles (from state.staffRoles) are added as implicit Staff-group
   profiles — giving the "owner adds a role, it picks up straight away" UX.
   Platform ids always win on collision. */
function getAllUserProfiles(state,platformCtx){
  const platform=platformCtx?.platformUserProfiles||[];
  const platformIds=new Set(platform.map(p=>p.id));
  const wsProfiles=(state?.staffRoles||[])
    .filter(r=>typeof r==="string"&&r.trim())
    .filter(r=>!platformIds.has(r))
    .map(r=>({
      id:r,
      name:_humanizeRoleId(r),
      nameAr:"",
      group:"Staff",
      source:"workspace", /* internal tag for UI differentiation if desired */
    }));
  return[...platform,...wsProfiles];
}

function staffViewCan(action,currentUser,state,platformCtx){
  if(!currentUser)return false;
  if(currentUser.role==="owner")return true;
  /* Universal staff capabilities: any Staff-group user (platform or workspace)
     gets leave + own-profile access by default. Prevents new custom roles
     from being unable to access basic self-service until perms are configured. */
  if(UNIVERSAL_STAFF_VIEW_CAPABILITIES.has(action)&&isStaffUser(currentUser,state,platformCtx))return true;
  const platDefaults=platformCtx?.platformStaffViewPermissions||{};
  const wsOverrides=state.staffViewPermissions||{};
  const effective={...STAFF_VIEW_DEFAULT_PERMS,...platDefaults,...wsOverrides};
  const allowed=effective[action]||[];
  /* "*staff" wildcard: grants this capability to any Staff-group user —
     platform-defined or workspace-defined alike. Keeps the design role-
     agnostic. Owners restrict by writing an explicit role list via the
     Staff Workspace Access matrix, which completely replaces this
     wildcard (the matrix never emits "*staff"). */
  if(allowed.includes("*staff")&&isStaffUser(currentUser,state,platformCtx))return true;
  return allowed.includes(currentUser.role);
}

/* "Is this user a staff user?" — true when their profile is in the Staff
   group, using the merged platform + workspace profile list. Falls back to
   the legacy "driver"/"maid" literal role strings so existing seeded users
   keep working even if staffRoles got emptied. */
function isStaffUser(currentUser,state,platformCtx){
  if(!currentUser)return false;
  if(currentUser.role==="owner"||currentUser.role==="family_manager")return false;
  const profile=getAllUserProfiles(state,platformCtx).find(p=>p.id===currentUser.role);
  if(profile?.group==="Staff")return true;
  return currentUser.role==="driver"||currentUser.role==="maid";
}

/* ── Task Taxonomy helpers ──────────────────────────────────
   Three-layer merge: seed → platform → workspace (same shape as
   staffViewCan's {...DEFAULTS, ...platform, ...workspace} above).
   Each entry is tagged with source:"seed"|"platform"|"workspace"
   for UI badge + edit-permission decisions. Disabled entries
   (enabled === false) are dropped from the effective result.
   Parents are emitted before their children so UIs can render
   grouped dropdowns without re-sorting. */
function _mergeTaxonomy(state,platformCtx){
  const seeded=(TASK_TAXONOMY_SEED||[]).map(e=>({...e,source:"seed"}));
  const platform=(platformCtx?.taskTaxonomy||[]).map(e=>({...e,source:"platform"}));
  const workspace=(state?.taskTaxonomy||[]).map(e=>({...e,source:"workspace"}));
  const byId=new Map();
  for(const e of seeded)    byId.set(e.id,e);
  for(const e of platform)  byId.set(e.id,{...(byId.get(e.id)||{}),...e,source:"platform"});
  for(const e of workspace) byId.set(e.id,{...(byId.get(e.id)||{}),...e,source:"workspace"});
  return Array.from(byId.values());
}

function getEffectiveTaxonomy(state,platformCtx,module){
  const merged=_mergeTaxonomy(state,platformCtx);
  const active=merged.filter(e=>e.enabled!==false&&(!module||e.module===module));
  /* Parents first, then children of each parent (stable within group).
     Children whose parent is missing or disabled are dropped — matches
     the "disable to hide" intent from plan §2.2 rule 4. */
  const roots=active.filter(e=>!e.parent);
  const rootIds=new Set(roots.map(r=>r.id));
  const kids =active.filter(e=>e.parent&&rootIds.has(e.parent));
  const out=[];
  for(const r of roots){
    out.push(r);
    for(const c of kids) if(c.parent===r.id) out.push(c);
  }
  return out;
}

function getTaxonomyEntry(state,platformCtx,id){
  if(!id)return null;
  return _mergeTaxonomy(state,platformCtx).find(e=>e.id===id)||null;
}

function getChildTaxonomy(state,platformCtx,parentId){
  if(!parentId)return [];
  return _mergeTaxonomy(state,platformCtx)
    .filter(e=>e.parent===parentId&&e.enabled!==false);
}

/* ── Facility Permission Keys ── */
const DEFAULT_ROOM_PRESETS=[
  {name:"Master Bedroom",nameAr:"غرفة النوم الرئيسية",items:["AC Unit","Water Heater","Light Fixtures"]},
  {name:"Bedroom",nameAr:"غرفة نوم",items:["AC Unit","Light Fixtures"]},
  {name:"Kitchen",nameAr:"مطبخ",items:["AC Unit","Water Heater","Exhaust Fan","Dishwasher","Oven","Refrigerator"]},
  {name:"Bathroom",nameAr:"حمام",items:["Water Heater","Exhaust Fan","Light Fixtures"]},
  {name:"Living Room",nameAr:"غرفة المعيشة",items:["AC Unit","Light Fixtures"]},
  {name:"Dining Room",nameAr:"غرفة الطعام",items:["AC Unit","Light Fixtures"]},
  {name:"Majlis",nameAr:"مجلس",items:["AC Unit","Light Fixtures"]},
  {name:"Garage",nameAr:"مرآب",items:["Garage Door","Light Fixtures"]},
  {name:"Laundry Room",nameAr:"غرفة الغسيل",items:["Washing Machine","Dryer","Water Heater"]},
  {name:"Maid's Room",nameAr:"غرفة الخادمة",items:["AC Unit","Water Heater","Light Fixtures"]},
  {name:"Driver's Room",nameAr:"غرفة السائق",items:["AC Unit","Water Heater","Light Fixtures"]},
  {name:"Guard Room",nameAr:"غرفة الحارس",items:["AC Unit","Light Fixtures"]},
  {name:"Pool Area",nameAr:"منطقة المسبح",items:["Pool Pump","Pool Heater","Light Fixtures"]},
  {name:"Garden",nameAr:"حديقة",items:["Irrigation System","Light Fixtures"]},
  {name:"Roof",nameAr:"سطح",items:["Water Tank","AC Outdoor Units","Satellite Dish"]},
  {name:"Elevator",nameAr:"مصعد",items:["Elevator Motor"]},
  {name:"Basement",nameAr:"قبو",items:["Sump Pump","Light Fixtures"]},
  {name:"Office",nameAr:"مكتب",items:["AC Unit","Light Fixtures"]},
];
const FACILITY_PERM_ACTIONS=["viewMaintenance","addMaintenance","editMaintenance","manageMaintenance"];
const FACILITY_DEFAULT_PERMS={viewMaintenance:["owner","family_manager","staff_handyman"],addMaintenance:["owner","family_manager","staff_handyman"],editMaintenance:["owner","family_manager"],manageMaintenance:["owner"]};

function staffCan(action,currentUser,state,platformCtx){
  if(!currentUser)return false;
  const profile=(platformCtx?.platformUserProfiles||[]).find(p=>p.id===currentUser.role);
  if(profile?.id==="owner")return true;
  const platDefaults=platformCtx?.platformStaffPermissions||{};
  const wsOverrides=state.staffPermissions||{};
  const effective={...STAFF_DEFAULT_PERMS,...platDefaults,...wsOverrides};
  const allowed=effective[action]||[];
  return allowed.includes(currentUser.role);
}

/* Debug version that returns the full resolution chain for troubleshooting */
function staffCanDebug(action,currentUser,state,platformCtx){
  const result={action,user:currentUser?.name,role:currentUser?.role,granted:false,reason:"",chain:[]};
  if(!currentUser){result.reason="No current user";return result;}
  const profile=(platformCtx?.platformUserProfiles||[]).find(p=>p.id===currentUser.role);
  if(profile?.id==="owner"){result.granted=true;result.reason="Owner bypass";return result;}
  const defaults=STAFF_DEFAULT_PERMS[action]||[];
  result.chain.push({layer:"default",roles:[...defaults]});
  const platDefaults=(platformCtx?.platformStaffPermissions||{})[action];
  if(platDefaults)result.chain.push({layer:"platform",roles:[...platDefaults]});
  const wsOverride=(state.staffPermissions||{})[action];
  if(wsOverride)result.chain.push({layer:"workspace",roles:[...wsOverride]});
  const effective={...STAFF_DEFAULT_PERMS,...(platformCtx?.platformStaffPermissions||{}),...(state.staffPermissions||{})};
  const allowed=effective[action]||[];
  result.chain.push({layer:"effective",roles:[...allowed]});
  result.granted=allowed.includes(currentUser.role);
  result.reason=result.granted?`Role "${currentUser.role}" found in effective roles`:`Role "${currentUser.role}" not in [${allowed.join(", ")}]`;
  return result;
}

/* ── Income Permission Keys ── */
const INCOME_PERM_ACTIONS=["viewIncome","addIncome","editIncome","deleteIncome"];
const INCOME_DEFAULT_PERMS={viewIncome:["owner"],addIncome:["owner"],editIncome:["owner"],deleteIncome:["owner"]};

/* ── Expense Permission Keys ── */
const EXPENSE_PERM_ACTIONS=["viewExpenses","addExpense","editExpense","deleteExpense","approveExpense"];
const EXPENSE_DEFAULT_PERMS={viewExpenses:["owner"],addExpense:["owner"],editExpense:["owner"],deleteExpense:["owner"],approveExpense:["owner"]};

/* ── Investment Permission Keys ── */
const INVESTMENT_PERM_ACTIONS=["viewInvestments","addInvestment","editInvestment","deleteInvestment"];
const INVESTMENT_DEFAULT_PERMS={viewInvestments:["owner"],addInvestment:["owner"],editInvestment:["owner"],deleteInvestment:["owner"]};

/* ── Investment Category Defaults ── */
const LIQUIDITY_OPTIONS=["High","Medium","Low"];
const DEFAULT_INV_CATS=[
  {name:"Public Equity",listed:true,liquidity:"High"},
  {name:"Private Equity",listed:false,liquidity:"Low"},
  {name:"Real Estate",listed:false,liquidity:"Low"},
  {name:"Bonds",listed:true,liquidity:"Medium"},
  {name:"Funds",listed:true,liquidity:"Medium"},
  {name:"Cash",listed:false,liquidity:"High"},
];
/* Normalize legacy string[] categories to {name,listed,liquidity}[] */
function normalizeInvCats(cats){
  if(!cats||!cats.length)return[];
  return cats.map(c=>{
    if(typeof c==="string")return{name:c,listed:false,liquidity:"Medium"};
    return{...c,liquidity:c.liquidity||(c.listed?"High":"Medium")};
  });
}

function incomeCan(action,currentUser,state,platformCtx){
  if(!currentUser)return false;
  if(currentUser.role==="owner")return true;
  const platDefaults=platformCtx?.platformIncomePermissions||{};
  const wsOverrides=state.incomePermissions||{};
  const effective={...INCOME_DEFAULT_PERMS,...platDefaults,...wsOverrides};
  const allowed=effective[action]||[];
  return allowed.includes(currentUser.role);
}

function expenseCan(action,currentUser,state,platformCtx){
  if(!currentUser)return false;
  if(currentUser.role==="owner")return true;
  const platDefaults=platformCtx?.platformExpensePermissions||{};
  const wsOverrides=state.expensePermissions||{};
  const effective={...EXPENSE_DEFAULT_PERMS,...platDefaults,...wsOverrides};
  const allowed=effective[action]||[];
  return allowed.includes(currentUser.role);
}

function investmentCan(action,currentUser,state,platformCtx){
  if(!currentUser)return false;
  if(currentUser.role==="owner")return true;
  const platDefaults=platformCtx?.platformInvestmentPermissions||{};
  const wsOverrides=state.investmentPermissions||{};
  const effective={...INVESTMENT_DEFAULT_PERMS,...platDefaults,...wsOverrides};
  const allowed=effective[action]||[];
  return allowed.includes(currentUser.role);
}

function facilityCan(action,currentUser,state){
  if(!currentUser)return false;
  if(currentUser.role==="owner")return true;
  const effective=state.facilityPermissions||FACILITY_DEFAULT_PERMS;
  const allowed=effective[action]||[];
  if(allowed.includes(currentUser.role))return true;
  return allowed.includes(currentUser.id);
}

/* ── Supplier Permission Keys ── */
const SUPPLIER_PERM_ACTIONS=["viewSuppliers","addSuppliers","editSuppliers","manageSuppliers"];
const SUPPLIER_DEFAULT_PERMS={viewSuppliers:["owner","family_manager","staff_handyman","staff_driver","staff_maid"],addSuppliers:["owner","family_manager"],editSuppliers:["owner","family_manager"],manageSuppliers:["owner"]};

function supplierCan(action,currentUser,state,platformCtx){
  if(!currentUser)return false;
  if(currentUser.role==="owner")return true;
  const platDefaults=platformCtx?.platformSupplierPermissions||{};
  const wsOverrides=state.supplierPermissions||{};
  const effective={...SUPPLIER_DEFAULT_PERMS,...platDefaults,...wsOverrides};
  const allowed=effective[action]||[];
  if(allowed.includes(currentUser.role))return true;
  return allowed.includes(currentUser.id);
}

/* ── Fleet Permission Keys ── */
const FLEET_PERM_ACTIONS=["viewFleet","addVehicle","editVehicle","manageFleet"];
const FLEET_DEFAULT_PERMS={viewFleet:["owner","family_manager","staff_driver"],addVehicle:["owner","family_manager"],editVehicle:["owner","family_manager"],manageFleet:["owner"]};

/* Registration/Istimara expiry read helper — handles legacy `istamaraExpiry` field */
function vehRegExpiry(v){return v?.regExpiry||v?.istamaraExpiry||"";}

function fleetCan(action,currentUser,state,platformCtx){
  if(!currentUser)return false;
  if(currentUser.role==="owner")return true;
  const platDefaults=platformCtx?.platformFleetPermissions||{};
  const wsOverrides=state.fleetPermissions||{};
  const effective={...FLEET_DEFAULT_PERMS,...platDefaults,...wsOverrides};
  const allowed=effective[action]||[];
  if(allowed.includes(currentUser.role))return true;
  return allowed.includes(currentUser.id);
}

/* ── Transport Permission Keys ── */
const TRANSPORT_PERM_ACTIONS=["viewTransport","bookTransport","editTransport","manageTransport"];
const TRANSPORT_DEFAULT_PERMS={viewTransport:["owner","family_manager","staff_driver"],bookTransport:["owner","family_manager"],editTransport:["owner","family_manager"],manageTransport:["owner"]};

function transportCan(action,currentUser,state,platformCtx){
  if(!currentUser)return false;
  if(currentUser.role==="owner")return true;
  const platDefaults=platformCtx?.platformTransportPermissions||{};
  const wsOverrides=state.transportPermissions||{};
  const effective={...TRANSPORT_DEFAULT_PERMS,...platDefaults,...wsOverrides};
  const allowed=effective[action]||[];
  if(allowed.includes(currentUser.role))return true;
  return allowed.includes(currentUser.id);
}

/* ── Project Permission Keys ── */
const PROJECT_PERM_ACTIONS=["viewProjects","addProject","editProject","deleteProject"];
const PROJECT_DEFAULT_PERMS={viewProjects:["owner","family_manager"],addProject:["owner","family_manager"],editProject:["owner","family_manager"],deleteProject:["owner"]};

function projectCan(action,currentUser,state,platformCtx,project){
  if(!currentUser)return false;
  if(currentUser.role==="owner")return true;
  const platDefaults=platformCtx?.platformProjectPermissions||{};
  const wsOverrides=state.projectPermissions||{};
  const effective={...PROJECT_DEFAULT_PERMS,...platDefaults,...wsOverrides};
  const allowed=effective[action]||[];
  const hasRolePerm=allowed.includes(currentUser.role)||allowed.includes(currentUser.id);
  if(!hasRolePerm)return false;
  /* Project-level membership gating: if the project has workspace user members,
     only those members (+ owner) can view/edit it. If no user members, it's open
     to anyone with the workspace-level role permission. */
  if(project&&(action==="viewProjects"||action==="editProject"||action==="deleteProject")){
    const userMembers=(project.people||[]).filter(pp=>pp.type==="user");
    if(userMembers.length>0&&!userMembers.some(pp=>pp.userId===currentUser.id))return false;
  }
  return true;
}

/* ── Smart Home Permission Keys ── */
const SMART_HOME_PERM_ACTIONS=["viewSmartHome","controlDevices","manageConnections"];
const SMART_HOME_DEFAULT_PERMS={viewSmartHome:["owner"],controlDevices:["owner"],manageConnections:["owner"]};

function smartHomeCan(action,currentUser,state,platformCtx){
  if(!currentUser)return false;
  if(currentUser.role==="owner")return true;
  const platDefaults=platformCtx?.platformSmartHomePermissions||{};
  const wsOverrides=state.smartHomePermissions||{};
  const effective={...SMART_HOME_DEFAULT_PERMS,...platDefaults,...wsOverrides};
  const allowed=effective[action]||[];
  if(allowed.includes(currentUser.role))return true;
  return allowed.includes(currentUser.id);
}

/* ── Staff helpers ── */
function getStaffApprovers(authorityKey,state,platformCtx){
  /* For leave/loan approvals: derive from permissions matrix.
     For salary notifications: use explicit salaryApprovers list. */
  const auth=state.staffAuthorities||{};
  if(authorityKey==="salaryApprovers"){
    const approverIds=auth.salaryApprovers||[];
    return (state.users||[]).filter(u=>u.active&&(u.role==="owner"||approverIds.includes(u.id)));
  }
  /* Map authority key to permission action */
  const permMap={leaveApprovers:"approveLeave",loanApprovers:"approveLoans"};
  const action=permMap[authorityKey];
  if(!action)return (state.users||[]).filter(u=>u.active&&u.role==="owner");
  const platPerms=platformCtx?.platformStaffPermissions||{};
  const wsPerms=state.staffPermissions||{};
  const effective={...STAFF_DEFAULT_PERMS,...platPerms,...wsPerms};
  const allowedProfiles=effective[action]||[];
  return (state.users||[]).filter(u=>u.active&&(u.role==="owner"||allowedProfiles.includes(u.role)));
}

function matchStaffRecord(staff,user){
  return (staff||[]).find(s=>s.userId===user.id)
    ||(staff||[]).find(s=>s.role===user.role&&(s.name.toLowerCase().includes(user.name.toLowerCase())||user.name.toLowerCase().includes(s.name.toLowerCase())))
    ||(staff||[]).find(s=>s.role===user.role);
}

function calcLoanDeductions(loans){
  const active=(loans||[]).filter(l=>l.status==="active");
  return active.reduce((a,l)=>a+Math.round(((parseFloat(l.amount)||0)/(parseInt(l.deductMonths)||1))*100)/100,0);
}

function findSalaryCat(expenseCats){
  return expenseCats.find(c=>c.name==="Staff Salaries")||expenseCats.find(c=>c.parent&&!expenseCats.find(p=>p.id===c.parent))||expenseCats[0];
}

export {
  _locCheck, canViewTask, canEditTask,
  STAFF_PERM_ACTIONS, STAFF_DEFAULT_PERMS,
  STAFF_VIEW_ACTIONS, STAFF_VIEW_DEFAULT_PERMS,
  DEFAULT_ROOM_PRESETS, FACILITY_PERM_ACTIONS, FACILITY_DEFAULT_PERMS,
  staffCan, staffCanDebug, staffViewCan, isStaffUser, getAllUserProfiles,
  INCOME_PERM_ACTIONS, INCOME_DEFAULT_PERMS,
  EXPENSE_PERM_ACTIONS, EXPENSE_DEFAULT_PERMS,
  INVESTMENT_PERM_ACTIONS, INVESTMENT_DEFAULT_PERMS,
  incomeCan, expenseCan, investmentCan, facilityCan,
  SUPPLIER_PERM_ACTIONS, SUPPLIER_DEFAULT_PERMS, supplierCan,
  FLEET_PERM_ACTIONS, FLEET_DEFAULT_PERMS, fleetCan,
  TRANSPORT_PERM_ACTIONS, TRANSPORT_DEFAULT_PERMS, transportCan,
  PROJECT_PERM_ACTIONS, PROJECT_DEFAULT_PERMS, projectCan,
  SMART_HOME_PERM_ACTIONS, SMART_HOME_DEFAULT_PERMS, smartHomeCan,
  LIQUIDITY_OPTIONS, vehRegExpiry,
  getStaffApprovers, matchStaffRecord, calcLoanDeductions, findSalaryCat,
  getEffectiveTaxonomy, getTaxonomyEntry, getChildTaxonomy,
};
